import { memo, useEffect, useRef } from "react";
import type { PdfEngine } from "../lib/engine.ts";
import type { PageInfo } from "../lib/protocol.ts";

// ─────────────────────────── thumbnail ───────────────────────────

export const THUMB_WIDTH = 88;

export const ThumbView = memo(function ThumbView({
  engine,
  pageIndex,
  info,
  active,
  pageSelected = false,
  busy,
  canDelete,
  onClick,
  onRotate,
  onDelete,
}: {
  engine: PdfEngine | null;
  pageIndex: number;
  info: PageInfo;
  active: boolean;
  /** Part of the thumbnail multi-selection (batch page ops). */
  pageSelected?: boolean;
  /** A page op is in flight — park the tools. */
  busy: boolean;
  canDelete: boolean;
  /** Plain click navigates; Ctrl/Cmd toggles selection; Shift ranges. */
  onClick: (index: number, e: { ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }) => void;
  onRotate: (index: number, clockwise: boolean) => void;
  onDelete: (index: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendered = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !engine) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || rendered.current) return;
        rendered.current = true;
        void engine
          .render(pageIndex, THUMB_WIDTH / info.width)
          .then(({ width, height, pixels }) => {
            const c = canvasRef.current;
            if (!c) return;
            c.width = width;
            c.height = height;
            c.getContext("2d")?.putImageData(
              new ImageData(new Uint8ClampedArray(pixels), width, height),
              0,
              0
            );
          })
          .catch(() => {});
      },
      { rootMargin: "300px 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [engine, pageIndex, info.width]);

  const height = Math.round((info.height / info.width) * THUMB_WIDTH);
  const toolBtn =
    "flex h-5 w-5 items-center justify-center rounded bg-[color:var(--color-bg)]/90 text-[11px] shadow-sm hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40";
  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      onClick={(e) => onClick(pageIndex, e)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick(pageIndex, e);
      }}
      className={`group relative block w-full cursor-pointer rounded-sm border p-1 transition-colors ${
        pageSelected
          ? "border-[color:var(--color-accent)] bg-[color:var(--color-accent)]/10 ring-1 ring-[color:var(--color-accent)]"
          : active
            ? "border-[color:var(--color-accent)] bg-[color:var(--color-bg-subtle)]"
            : "border-transparent hover:border-[color:var(--color-border-strong)]"
      }`}
      aria-label={`Page ${pageIndex + 1}`}
    >
      <canvas
        ref={canvasRef}
        width={THUMB_WIDTH}
        height={height}
        className="mx-auto bg-white shadow-sm"
        style={{ width: THUMB_WIDTH, height }}
      />
      <div className="mt-1 text-center text-[10px] text-[color:var(--color-fg-muted)]">
        {pageIndex + 1}
      </div>
      {pageSelected && (
        <span
          className="absolute left-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-[color:var(--color-accent)] text-[10px] text-[color:var(--color-accent-fg)]"
          aria-hidden
        >
          ✓
        </span>
      )}
      {/* hover tools: rotate / delete */}
      <div className="absolute right-1.5 top-1.5 flex gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
        <button
          type="button"
          title="Rotate left"
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            onRotate(pageIndex, false);
          }}
          className={toolBtn}
        >
          <i className="ri-anticlockwise-line" aria-hidden />
        </button>
        <button
          type="button"
          title="Rotate right"
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            onRotate(pageIndex, true);
          }}
          className={toolBtn}
        >
          <i className="ri-clockwise-line" aria-hidden />
        </button>
        <button
          type="button"
          title="Delete page"
          disabled={busy || !canDelete}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(pageIndex);
          }}
          className={toolBtn}
        >
          <i className="ri-delete-bin-line" aria-hidden />
        </button>
      </div>
    </div>
  );
});

/** One insert-preview thumbnail. The pixels arrive once from the probe,
 *  so paint them on mount and leave the canvas alone after that. */
export const ThumbCanvas = memo(function ThumbCanvas({
  thumb,
}: {
  thumb: { width: number; height: number; pixels: ArrayBuffer };
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    c.width = thumb.width;
    c.height = thumb.height;
    c.getContext("2d")?.putImageData(
      new ImageData(new Uint8ClampedArray(thumb.pixels), thumb.width, thumb.height),
      0,
      0
    );
  }, [thumb]);
  return (
    <canvas
      ref={ref}
      className="block bg-white shadow-sm"
      style={{ width: 48, height: Math.round((thumb.height / thumb.width) * 48) }}
    />
  );
});
